import React, { useEffect, useState } from "react";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import closeIcon from "../../../../../../Assets/images/close-icon.svg";
import redIcon from "../../../../../../Assets/images/close-red.svg";
import calendarIcon from "../../../../../../Assets/images/calender.svg";
import Loader from "../../../../../../Context/Loader/Loader";
import apiClient from "../../../../../../Config/apiClient";
import "./AddTruckModal.scss"

const truckPath = process.env.REACT_APP_TC_TRUCK_BASE_PATH;
const DATE_MONTH_FORMAT = "MM-dd-yyyy";

const AddEditTruckModal = ({ show, onClose, onSave, truckData, newTruckId }) => {
    const [formData, setFormData] = useState({
        truck_id: "",
        vehicle_no: "",
        license_plate_no: "",
        insurance_no: "",
        truck_type: "Tri-Axle Dump Truck",
        cvor_expiry: "",
        insurance_expiry: "",
        documents: {
            cvor_doc: null,
            permit_doc: null,
            insurance_doc: null,
        },
        existingDocs: null
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [hoverDoc, setHoverDoc] = useState({ cvor: false, permit: false, insurance: false });

    useEffect(() => {
        const fetchNextTruckId = async () => {
            if (!truckData) {
                try {
                    const res = await apiClient.get(`${truckPath}/next-id`);
                    if (res.data.status === "SUCCESS") {
                        setFormData((prev) => ({ ...prev, truck_id: res.data.data.nextId }));
                    }
                } catch (err) {
                    console.error("Failed to fetch next truck ID", err);
                }
            }
        };
        fetchNextTruckId();
    }, [truckData]);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        if (truckData) {
            setFormData({
                truck_id: truckData.truck_id || "",
                vehicle_no: truckData.vehicle_no || "",
                license_plate_no: truckData.license_plate_no || "",
                insurance_no: truckData.insurance_no || "",
                truck_type: "Tri-Axle Dump Truck",
                cvor_expiry: truckData.cvor_expiry ? formatDate(truckData.cvor_expiry) : "",
                insurance_expiry: truckData.insurance_expiry ? formatDate(truckData.insurance_expiry) : "",
                documents: {
                    cvor_doc: null,
                    permit_doc: null,
                    insurance_doc: null,
                },
                existingDocs: {
                    cvor_doc: truckData.cvor_doc || null,
                    cvor_doc_url: truckData.cvor_doc_url || null,
                    permit_doc: truckData.permit_doc || null,
                    permit_doc_url: truckData.permit_doc_url || null,
                    insurance_doc: truckData.insurance_doc || null,
                    insurance_doc_url: truckData.insurance_doc_url || null,
                }
            });
        } else {
            setFormData((prev) => ({
                ...prev,
                truck_id: newTruckId || "",
                vehicle_no: "",
                license_plate_no: "",
                insurance_no: "",
                truck_type: "Tri-Axle Dump Truck",
                documents: {
                    cvor_doc: null,
                    permit_doc: null,
                    insurance_doc: null,
                },
                existingDocs: null
            }));
        }
        setErrors({});
    }, [truckData, newTruckId]);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (files && files[0]) {
            const file = files[0];
            if (file.size > 5 * 1024 * 1024) {
                setErrors((prev) => ({ ...prev, [name]: "File size must not exceed 5 MB." }));
                e.target.value = null;
                return;
            }
            if (file.type !== "application/pdf") {
                setErrors((prev) => ({ ...prev, [name]: "Only PDF files allowed" }));
                return;
            }
            setFormData((prev) => ({
                ...prev,
                documents: { ...prev.documents, [name]: file },
            }));
            setErrors((prev) => ({ ...prev, [name]: "" }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
            setErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handleRemoveFile = (field) => {
        // Allow clearing the field in UI for all documents
        const updates = {
            ...formData,
            documents: { ...formData.documents, [field]: null },
            existingDocs: formData.existingDocs ? {
                ...formData.existingDocs,
                [field]: null,
                [`${field}_url`]: null
            } : null
        };
        
        // If removing insurance doc, also clear insurance_expiry
        if (field === 'insurance_doc') {
            updates.insurance_expiry = "";
        }
        
        setFormData(updates);
        setErrors((prev) => ({ ...prev, [field]: "", ...(field === 'insurance_doc' && { insurance_expiry: "" }) }));

        const input = document.getElementById(field);
        if (input) input.value = "";
    };

    const validateForm = () => {
        const newErrors = {};
        const today = new Date();

        const isEdit = !!truckData;

        // Validate cvor_expiry (mandatory)
        if (!formData.cvor_expiry) {
            newErrors.cvor_expiry = "Vehicle Ownership expiry date is required";
        } else if (!isEdit) {
            const selectedDate = new Date(formData.cvor_expiry);
            if (selectedDate <= today) {
                newErrors.cvor_expiry = "Vehicle Ownership expiry date must be greater than today";
            }
        }

        // Insurance fields are optional as a group — if ANY one is provided, ALL are required
        const hasInsuranceDoc = !!(formData.documents.insurance_doc || formData.existingDocs?.insurance_doc);
        const hasInsuranceNo = !!formData.insurance_no;
        const hasInsuranceExpiry = !!formData.insurance_expiry;
        const anyInsurance = hasInsuranceDoc || hasInsuranceNo || hasInsuranceExpiry;

        if (anyInsurance) {
            if (!hasInsuranceDoc) {
                newErrors.insurance_doc = "WSIB Insurance document is required";
            }
            if (!hasInsuranceNo) {
                newErrors.insurance_no = "WSIB Insurance number is required";
            }
            if (!hasInsuranceExpiry) {
                newErrors.insurance_expiry = "WSIB Insurance expiry is required";
            } else if (!isEdit) {
                const selectedDate = new Date(formData.insurance_expiry);
                if (selectedDate <= today) {
                    newErrors.insurance_expiry = "WSIB Insurance expiry date must be greater than today";
                }
            }
        }

        if (!formData.vehicle_no) newErrors.vehicle_no = "Vehicle number is required";
        if (!formData.license_plate_no) newErrors.license_plate_no = "License plate number is required";

        const docFields = ["cvor_doc", "permit_doc"];
        const docLabels = {
            cvor_doc: "Vehicle Ownership document",
            permit_doc: "MTO Permit document",
        };

        docFields.forEach((field) => {
            const uploadedFile = formData.documents[field];
            const existingDoc = formData.existingDocs?.[field];
            if (!uploadedFile && !existingDoc) {
                newErrors[field] = `${docLabels[field]} is required`;
            }
        });

        // WSIB Insurance document is optional - no validation needed

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        try {
            setSubmitting(true);
            await onSave(formData);
        } finally {
            setSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal right rtm-modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.15)" }}>
            <div className="modal-dialog rtl-dialog">
                <div className="modal-content rtl-content animate__animated animate__fadeInRight rtl-overflow">
                    <div className="ajob-content">
                        <div className="cpf-top cpf-border">
                            <h2>{truckData ? "Edit Truck" : "Add Truck"}</h2>
                            <button className="m-close" onClick={onClose}>
                                <img src={closeIcon} alt="Close" />
                            </button>
                        </div>

                        <div className="modal-inner addtruck-form">
                            <div className="adddriver-form scroll-padding">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="cp-split">
                                            <label className="fi-label driver-lbl">ID <span style={{ color: "red" }}>*</span></label>
                                            <input type="text" className="common-input truckid-disable" value={truckData ? `T-${formData.truck_id}` : "AUTO GENERATED"} disabled />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className={`cp-split ${errors.vehicle_no ? "error" : ""}`}>
                                            <label className="fi-label driver-lbl">Vehicle Number <span style={{ color: "red" }}>*</span></label>
                                            <input type="text" className="common-input" name="vehicle_no" value={formData.vehicle_no} onChange={handleChange} />
                                            {errors.vehicle_no && <span className="error-text active">{errors.vehicle_no}</span>}
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className={`cp-split ${errors.license_plate_no ? "error" : ""}`}>
                                            <label className="fi-label driver-lbl">License Plate Number <span style={{ color: "red" }}>*</span></label>
                                            <input type="text" className="common-input" name="license_plate_no" value={formData.license_plate_no} onChange={handleChange} />
                                            {errors.license_plate_no && <span className="error-text active">{errors.license_plate_no}</span>}
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className={`cp-split ${errors.insurance_no ? "error" : ""}`}>
                                            <label className="fi-label driver-lbl">WSIB Insurance Number</label>
                                            <input type="text" className="common-input" name="insurance_no" value={formData.insurance_no} onChange={handleChange} />
                                            {errors.insurance_no && <span className="error-text active">{errors.insurance_no}</span>}
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="cp-split">
                                            <label className="fi-label driver-lbl">Truck Type <span style={{ color: "red" }}>*</span></label>
                                            <input type="text" className="common-input" value="Tri-Axle Dump Truck" disabled />
                                        </div>
                                    </div>
                                </div>
                                <div className="doc-cover">
                                    <h2 className="sub-head">Documents</h2>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">Vehicle Ownership <span style={{ color: "red" }}>*</span></label>
                                                <div className={`hsfile-upload ${errors.cvor_doc ? "error-border" : ""}`}>
                                                    <div className="doc-display">
                                                        <label 
                                                            className="file-upload-label" 
                                                            style={{ 
                                                                cursor: formData.existingDocs?.cvor_doc_url && !formData.documents.cvor_doc ? 'pointer' : 'default',
                                                                color: hoverDoc.cvor ? '#28a745' : 'inherit'
                                                            }}
                                                            title={formData.documents.cvor_doc?.name || (formData.existingDocs?.cvor_doc ? formData.existingDocs.cvor_doc.split('/').pop() : "")}
                                                            onClick={() => formData.existingDocs?.cvor_doc_url && !formData.documents.cvor_doc && window.open(formData.existingDocs.cvor_doc_url, '_blank')}
                                                            onMouseEnter={() => formData.existingDocs?.cvor_doc_url && !formData.documents.cvor_doc && setHoverDoc(prev => ({...prev, cvor: true}))}
                                                            onMouseLeave={() => setHoverDoc(prev => ({...prev, cvor: false}))}
                                                        >
                                                            {formData.documents.cvor_doc?.name || 
                                                             (formData.existingDocs?.cvor_doc ? 
                                                              formData.existingDocs.cvor_doc.split('/').pop() : 
                                                              "")}
                                                        </label>
                                                        {(formData.documents.cvor_doc || formData.existingDocs?.cvor_doc) && (
                                                            <img src={redIcon} alt="Remove" className="remove-icon" onClick={() => handleRemoveFile("cvor_doc")} />
                                                        )}
                                                    </div>
                                                    <label htmlFor="cvor" className="browse-btn" tabIndex="0" onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && document.getElementById('cvor')?.click()}>Browse</label>
                                                    <input key={formData.documents.cvor_doc ? formData.documents.cvor_doc.name : "cvor"} id="cvor" name="cvor_doc" type="file" accept=".pdf" onChange={handleChange} hidden />
                                                </div>
                                                {errors.cvor_doc && <span className="error-text active">{errors.cvor_doc}</span>}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">
                                                    Vehicle Ownership Expiry <span style={{ color: "red" }}>*</span>
                                                </label>
                                                <div style={{ position: 'relative' }}>
                                                    <DatePicker
                                                        selected={formData.cvor_expiry ? new Date(formData.cvor_expiry) : null}
                                                        onChange={(date) => {
                                                            setFormData((prev) => ({ ...prev, cvor_expiry: date ? date.toLocaleDateString('en-CA') : '' }));
                                                            setErrors((prev) => ({ ...prev, cvor_expiry: '' }));
                                                        }}
                                                        placeholderText="Select date"
                                                        className={`filter-date w-100 ${errors.cvor_expiry ? 'error' : ''}`}
                                                        dateFormat={DATE_MONTH_FORMAT}
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                        minDate={new Date()}
                                                    />
                                                    <span className="filter-calendar-icon">
                                                        <img src={calendarIcon} alt="calendar" />
                                                    </span>
                                                </div>
                                                {errors.cvor_expiry && (
                                                    <span className="error-text active">{errors.cvor_expiry}</span>
                                                )}
                                                {!errors.cvor_expiry && truckData && formData.cvor_expiry && new Date(formData.cvor_expiry) < new Date(new Date().setHours(0,0,0,0)) && (
                                                    <span style={{ color: "#d97706", fontSize: "12px" }}>⚠ This document has already expired.</span>
                                                )}
                                            </div>
                                        </div>


                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">WSIB Insurance</label>
                                                <div className={`hsfile-upload ${errors.insurance_doc ? "error-border" : ""}`}>
                                                    <div className="doc-display">
                                                        <label 
                                                            className="file-upload-label" 
                                                            style={{ 
                                                                cursor: formData.existingDocs?.insurance_doc_url && !formData.documents.insurance_doc ? 'pointer' : 'default',
                                                                color: hoverDoc.insurance ? '#28a745' : 'inherit'
                                                            }}
                                                            title={formData.documents.insurance_doc?.name || (formData.existingDocs?.insurance_doc ? formData.existingDocs.insurance_doc.split('/').pop() : "")}
                                                            onClick={() => formData.existingDocs?.insurance_doc_url && !formData.documents.insurance_doc && window.open(formData.existingDocs.insurance_doc_url, '_blank')}
                                                            onMouseEnter={() => formData.existingDocs?.insurance_doc_url && !formData.documents.insurance_doc && setHoverDoc(prev => ({...prev, insurance: true}))}
                                                            onMouseLeave={() => setHoverDoc(prev => ({...prev, insurance: false}))}
                                                        >
                                                            {formData.documents.insurance_doc?.name || 
                                                             (formData.existingDocs?.insurance_doc ? 
                                                              formData.existingDocs.insurance_doc.split('/').pop() : 
                                                              "")}
                                                        </label>
                                                        {(formData.documents.insurance_doc || formData.existingDocs?.insurance_doc) && (
                                                            <img src={redIcon} alt="Remove" className="remove-icon" onClick={() => handleRemoveFile("insurance_doc")} />
                                                        )}
                                                    </div>
                                                    <label htmlFor="insurance" className="browse-btn" tabIndex="0" onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && document.getElementById('insurance')?.click()}>Browse</label>
                                                    <input key={formData.documents.insurance_doc ? formData.documents.insurance_doc.name : "insurance"} id="insurance" name="insurance_doc" type="file" accept=".pdf" onChange={handleChange} hidden />
                                                </div>
                                                {errors.insurance_doc && <span className="error-text active">{errors.insurance_doc}</span>}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">WSIB Insurance Expiry</label>
                                                <div style={{ position: 'relative' }}>
                                                    <DatePicker
                                                        selected={formData.insurance_expiry ? new Date(formData.insurance_expiry) : null}
                                                        onChange={(date) => {
                                                            setFormData((prev) => ({ ...prev, insurance_expiry: date ? date.toLocaleDateString('en-CA') : '' }));
                                                            setErrors((prev) => ({ ...prev, insurance_expiry: '' }));
                                                        }}
                                                        placeholderText="Select date"
                                                        className={`filter-date w-100 ${errors.insurance_expiry ? 'error' : ''}`}
                                                        dateFormat={DATE_MONTH_FORMAT}
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                        minDate={new Date()}
                                                    />
                                                    <span className="filter-calendar-icon">
                                                        <img src={calendarIcon} alt="calendar" />
                                                    </span>
                                                </div>
                                                {errors.insurance_expiry && (
                                                    <span className="error-text active">{errors.insurance_expiry}</span>
                                                )}
                                                {!errors.insurance_expiry && truckData && formData.insurance_expiry && new Date(formData.insurance_expiry) < new Date(new Date().setHours(0,0,0,0)) && (
                                                    <span style={{ color: "#d97706", fontSize: "12px" }}>⚠ This document has already expired.</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">MTO Permit <span style={{ color: "red" }}>*</span></label>
                                                <div className={`hsfile-upload ${errors.permit_doc ? "error-border" : ""}`}>
                                                    <div className="doc-display">
                                                        <label 
                                                            className="file-upload-label" 
                                                            style={{ 
                                                                cursor: formData.existingDocs?.permit_doc_url && !formData.documents.permit_doc ? 'pointer' : 'default',
                                                                color: hoverDoc.permit ? '#28a745' : 'inherit'
                                                            }}
                                                            title={formData.documents.permit_doc?.name || (formData.existingDocs?.permit_doc ? formData.existingDocs.permit_doc.split('/').pop() : "")}
                                                            onClick={() => formData.existingDocs?.permit_doc_url && !formData.documents.permit_doc && window.open(formData.existingDocs.permit_doc_url, '_blank')}
                                                            onMouseEnter={() => formData.existingDocs?.permit_doc_url && !formData.documents.permit_doc && setHoverDoc(prev => ({...prev, permit: true}))}
                                                            onMouseLeave={() => setHoverDoc(prev => ({...prev, permit: false}))}
                                                        >
                                                            {formData.documents.permit_doc?.name || 
                                                             (formData.existingDocs?.permit_doc ? 
                                                              formData.existingDocs.permit_doc.split('/').pop() : 
                                                              "")}
                                                        </label>
                                                        {(formData.documents.permit_doc || formData.existingDocs?.permit_doc) && (
                                                            <img src={redIcon} alt="Remove" className="remove-icon" onClick={() => handleRemoveFile("permit_doc")} />
                                                        )}
                                                    </div>
                                                    <label htmlFor="permit_doc" className="browse-btn" tabIndex="0" onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            document.getElementById('permit_doc')?.click();
                                                        }
                                                    }}>Browse</label>
                                                    <input key={formData.documents.permit_doc ? formData.documents.permit_doc.name : "permit_doc"} id="permit_doc" name="permit_doc" type="file" accept=".pdf" onChange={handleChange} hidden />
                                                </div>
                                                {errors.permit_doc && <span className="error-text active">{errors.permit_doc}</span>}
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className="cp-split">
                                                <label className="fi-label driver-lbl">&nbsp;</label>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                <div className="m-button">
                                    <button className="cp-button save-btn" onClick={handleSubmit} disabled={submitting} >
                                        {submitting ? <Loader /> : truckData ? "Update" : "Save"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddEditTruckModal;